import { unwrapResult } from '@reduxjs/toolkit'
import { ErrorCode, Country, Checkout } from '@afterpaytouch/portal-api/types'
import { Route } from '../../../config/router'
import {
  BiometricsCheckTrigger,
  CrossRegionRedirection,
  OtpLoginRouteAndTriggerResult,
  ConvergenceRollout,
  BiometricEligibility,
  isPublicResponse,
} from '../../../model'
import {
  AsyncAction,
  getErrorFromPublicResponseWithSource,
  handleEmailLookup,
  mobileLookup,
  setCheckoutError,
  setCohort,
  crossRegionUserSignIn,
} from '../../../state'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { evaluateCohort } from '../../../state/convergence/helper'
import { PublicResponse } from '@afterpay/verify-service-api'
import { validateEmailFormat } from '../validation'
import { deepMergeLookupResponse as mergeLookupResponses, InitialiseLookup } from '../lookupMerger'
import { determineAuthRoute, checkBiometricEligibility, InitialiseNextRoute } from '../routing'
import { createPhoneNumberUtils } from '@afterpaytouch/core'

export interface SequentialLookupRequest {
  email: string
  mobileNumber: string | null
  checkout: Checkout
  country: Country
  redirectCountry?: Country
  biometricCheckTrigger: BiometricsCheckTrigger
  isBiometricEligiblePayload: BiometricEligibility
  otpLoginRouteAndTrigger: OtpLoginRouteAndTriggerResult
  rollout: ConvergenceRollout
}

type SequentialLookupResult = InitialiseNextRoute | CrossRegionRedirection | PublicResponse | { route: Route }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const performSequentialLookup = async (request: SequentialLookupRequest, dispatch: (action: any) => any): Promise<SequentialLookupResult> => {
  let initialiseLookupResponse: InitialiseLookup = {
    existing: false,
    merchantId: request.checkout.merchant.id,
    merchantOrderSource: request.checkout.merchant.source,
  }

  const { email, mobileNumber, checkout, country, redirectCountry, biometricCheckTrigger, isBiometricEligiblePayload, otpLoginRouteAndTrigger, rollout } =
    request

  const triggerOtpLoginIfEligibleAndReturnLoginRoute = otpLoginRouteAndTrigger

  if (!validateEmailFormat(email)) {
    return dispatch(redirectToEnterEmail({ email, mobileNumber, redirectCountry, lookup: initialiseLookupResponse }))
  }

  try {
    const emailLookupResponse = await dispatch(handleEmailLookup(email)) // will also invoke checkCrossBorder
    const { parseToE164NumberWithoutPlusPrefix } = createPhoneNumberUtils(country)

    if ('existing' in emailLookupResponse) {
      const {
        cashtag,
        existing,
        passwordExists,
        trustedSession = false,
        shadowAccountExists,
        cashEligibilitySignals,
        creationDate,
        loginType,
      } = emailLookupResponse
      initialiseLookupResponse = {
        cashtag,
        existing,
        shadowAccountExists,
        email,
        merchantId: checkout.merchant.id,
        cashEligibilitySignals,
        cashCreationDate: creationDate,
        merchantOrderSource: checkout.merchant.source,
        loginType,
      }

      if (existing === true && passwordExists === true) {
        const { cohort } = evaluateCohort(
          cashtag,
          existing,
          rollout,
          email,
          shadowAccountExists,
          checkout.merchant.id.toString(),
          country,
          checkout.merchant.source
        )
        dispatch(setCohort(cohort))

        const isBiometricsRegisteredAndEligible = await checkBiometricEligibility({
          biometricCheckTrigger,
          isBiometricEligiblePayload,
          cohort,
          isNewAccount: false,
          email,
          mobileNumber,
        })

        return determineAuthRoute({
          existing,
          passwordExists,
          trustedSession,
          isBiometricsEligible: isBiometricsRegisteredAndEligible,
          otpTrigger: triggerOtpLoginIfEligibleAndReturnLoginRoute,
          lookup: initialiseLookupResponse,
        })
      }
      if (existing === true) {
        return { route: Route.ENTER_EMAIL, lookup: initialiseLookupResponse }
      }

      if (existing !== true && !isUndefinedOrEmpty(parseToE164NumberWithoutPlusPrefix(mobileNumber ?? ''))) {
        try {
          const mobileLookupResponse = unwrapResult(
            await dispatch(
              mobileLookup({
                mobile: parseToE164NumberWithoutPlusPrefix(mobileNumber ?? ''),
                isFromInitialiseCheckout: true,
              })
            )
          )

          if (isPublicResponse(mobileLookupResponse)) {
            dispatch(setCheckoutError(getErrorFromPublicResponseWithSource({ ...mobileLookupResponse, source: 'action' })))
            return { route: Route.TERMINAL_ERROR }
          }

          const { cashtag, existing, shadowAccountExists, passwordExists: mobilePasswordExists } = mobileLookupResponse
          initialiseLookupResponse = mergeLookupResponses(initialiseLookupResponse, mobileLookupResponse)

          if ('error' in mobileLookupResponse || ('existing' in mobileLookupResponse && mobileLookupResponse.existing !== true)) {
            return dispatch(redirectToEnterEmail({ email, mobileNumber, redirectCountry, lookup: initialiseLookupResponse }))
          } else {
            const { cohort } = evaluateCohort(
              cashtag,
              existing,
              rollout,
              undefined,
              shadowAccountExists,
              checkout.merchant.id.toString(),
              country,
              checkout.merchant.source
            )
            dispatch(setCohort(cohort))

            const isBiometricRegisteredAndEligible = await checkBiometricEligibility({
              biometricCheckTrigger,
              isBiometricEligiblePayload,
              cohort,
              isNewAccount: true,
              email,
              mobileNumber,
            })

            return determineAuthRoute({
              existing,
              passwordExists: mobilePasswordExists === true,
              trustedSession,
              isBiometricsEligible: isBiometricRegisteredAndEligible,
              otpTrigger: triggerOtpLoginIfEligibleAndReturnLoginRoute,
              lookup: initialiseLookupResponse,
            })
          }
        } catch {
          return { route: Route.ENTER_EMAIL }
        }
      }
      return dispatch(redirectToEnterEmail({ email, mobileNumber, redirectCountry, lookup: initialiseLookupResponse }))
    } else if (
      'error' in emailLookupResponse &&
      'errorCode' in emailLookupResponse &&
      emailLookupResponse.errorCode === ErrorCode.UnsupportedMerchantTradingCountry
    ) {
      dispatch(setCheckoutError(emailLookupResponse))
      return { route: Route.ENTER_EMAIL }
    }
    return emailLookupResponse
  } catch {
    return { route: Route.ENTER_EMAIL }
  }
}

const redirectToEnterEmail =
  ({ email, mobileNumber, redirectCountry, lookup }): AsyncAction<SequentialLookupResult> =>
  async (dispatch) => {
    if (typeof redirectCountry !== 'undefined') {
      return unwrapResult(
        await dispatch(
          crossRegionUserSignIn({
            consumerEmail: email === '' ? '-' : email, // hack: backend chokes on blank emails
            consumerCountryCode: redirectCountry,
            consumerMobile: mobileNumber ?? '',
          })
        )
      )
    }
    return { route: Route.ENTER_EMAIL, lookup }
  }
